import { mapState, mapMutations } from 'vuex'
import { randomString } from '@/utils/Common'
import { Device } from "@/utils/Capacitor";
import { Capacitor } from '@capacitor/core';
import { isIosApp as _isIosApp, paymentsEnabled as _paymentsEnabled } from '@/utils/Platform'
import { taxonomyMixin } from '@/mixins/taxonomyMixin'
import { updateAssignedRoutine } from '@/utils/Routine'
import { loadUnreadRecommendations } from '@/utils/AiRecommendations'
import { loadUnreadAlerts } from '@/utils/AiAlerts'

export const accountMixin = {
    mixins: [taxonomyMixin],
    data() {
        return {
            isIphone: false,
            unreadRecommendations: 0,
            unreadAlerts: 0,
            unreadAlertsList: [],
        }
    },
    computed: {
        ...mapState(['currentUser', 'account', 'preventRouteLeaveFn', 'dialogStack', 'brandLogo']),
        currentQuery() {
            const vm = this
            let query = {}
            if (Object.keys(vm.$route.query).length > 0) query = vm.$route.query
            return query
        },
        isMobile() {
            return this.$vuetify.breakpoint.smAndDown
        },
        appName() {
            return process.env.VUE_APP_NAME
        },
        datePickerLocale() {
            return this.$i18n.locale === 'en' ? 'en' : 'es'
        },
        weekStartDay() {
            return 1
        },
        imgPlaceholder() {
            return this.account?.logo?.url || this.brandLogo || require('@/assets/img/logo-placeholder.png')
        },
        tooltipBg() {
            const theme = this.$vuetify.theme
            return theme.dark ? theme.themes.dark.bg : theme.themes.light.surface
        },
        isFreePlan(vm = this) {
            return vm.account?.stripeAccount?.currentSubscription?.isFree === true
        },
        accountType(vm = this) {
            let accountType = vm.account?.accountType || ''
            if (accountType === 'coach_company') accountType = 'coach'
            return accountType
        },
        isCompany(vm = this) {
            return vm.accountType === 'company'
        },
        isIosApp() {
            return _isIosApp()
        },
        paymentsEnabled() {
            return _paymentsEnabled()
        },
        versionAppCode() {
            return Number(process.env.VUE_APP_VERSION_APP || 0)
        },
        _noAvailableName() {
            return this.$t('common.not_available_short')
        }
    },
    async mounted() {
        this.isIphone = await Device.isIphone()
        if (this.currentUser && (this.accountType === 'coach' || this.accountType === 'coach_company')) {
            const unread = await loadUnreadRecommendations(this.currentUser?.uid)
            this.unreadRecommendations = unread.length
            const alerts = await loadUnreadAlerts(this.currentUser?.uid)
            this.unreadAlerts = alerts.length
            this.unreadAlertsList = alerts
        }
    },
    methods: {
        ...mapMutations(['showSnackBar', 'setPreventRouteLeaveFn']),
        randomString,
        weekdayLetter(date) {
            const m = this.$moment(date)
            if (this.$i18n.locale !== 'en' && m.day() === 3) return 'X'
            return m.format('dd').charAt(0).toUpperCase()
        },
        toast(message = '', color = 'success') {
            const vm = this
            vm.showSnackBar({ text: message, color: color })
        },
        notifyAssignedRoutineGone(assignedRoutineId) {
            const vm = this
            const key = vm.accountType === 'student' ? 'routines.replaced_by_coach' : 'routines.assigned_routine_gone'
            vm.toast(vm.$t(key), 'warning')
            vm.$root.$emit('assignedRoutineGone', assignedRoutineId)
        },
        async saveAssignedRoutine(assignedRoutineId, payload, collection = 'assignedRoutines') {
            const vm = this
            const saved = await updateAssignedRoutine(assignedRoutineId, payload, collection)
            if (!saved) vm.notifyAssignedRoutineGone(assignedRoutineId)
            return saved
        },
        async withLoading(fn, loadingKey = 'loading') {
            const vm = this
            if (loadingKey) vm[loadingKey] = true
            try {
                return await fn()
            } catch (error) {
                vm.toast(vm.$t('common.error_generic'), 'error')
                throw error
            } finally {
                if (loadingKey) vm[loadingKey] = false
            }
        },
        onBackPress(fn = () => { }) {
            const vm = this
            vm.setPreventRouteLeaveFn(fn)
        },
        clearOnBack() {
            const vm = this
            vm.setPreventRouteLeaveFn(null)
        },
        goHome(replace = false) {
            const vm = this
            if (vm.accountType) return vm.$router.push({ name: `${vm.accountType}.home`, replace: replace })
        },
        pushRoute(location) {
            return this.navigateRoute(location, false)
        },
        replaceRoute(location) {
            return this.navigateRoute(location, true)
        },
        navigateRoute(location, replace = false) {
            const vm = this
            const go = () => replace
                ? vm.$router.replace(location).catch(() => { })
                : vm.$router.push(location).catch(() => { })
            const depth = vm.$store.state.dialogStack.length
            if (depth <= 0) return go()
            if (Capacitor.isNativePlatform()) {
                vm.$store.commit('clearDialogs')
                return go()
            }
            let done = false
            const finish = () => {
                if (done) return
                done = true
                clearTimeout(fallback)
                window.removeEventListener('popstate', finish)
                go()
            }
            const fallback = setTimeout(finish, 400)
            window.addEventListener('popstate', finish)
            vm.$store.commit('clearDialogs')
        },
        getYoutubeImgUrl(youtubeUrl) {
            return `https://img.youtube.com/vi/${youtubeUrl}/hqdefault.jpg`
        },
        _checkNeedReloadBind(bind = [], coachId = null) {
            return bind.length === 0 || bind?.[0]?.coachId !== coachId
        }
    },
    watch: {}
}

export const links = {

    data(vm) {
        return {
            coach: [
                { ref: 'home', title: vm.$t('common.home'), icon: '$home', link: { name: 'coach.home' } },
                { ref: 'students', title: vm.$tc('student.student', 2), icon: '$account_multiple', link: { name: 'coach.students' }, count: 0, badge: 'messages' },
                { ref: 'library', title: vm.$t('common.library'), icon: '$training', link: { name: 'coach.library' } },
                { ref: 'calendar', title: vm.$t('common.calendar'), icon: '$calendar', link: { name: 'coach.calendar' } },
                { ref: 'statistics', title: vm.$t('common.statistics'), icon: '$statistics', link: { name: 'coach.statistics' } },
                { ref: 'evaluations', title: vm.$t('common.evaluations'), icon: 'mdi-book-open-variant', link: { name: 'coach.evaluations' } },
                { ref: 'profile', title: vm.$t('common.profile'), icon: '$account_circle', link: { name: 'coach.profile' } }
            ],
            studentWithCoach: [
                { ref: 'home', title: vm.$t('common.home'), icon: '$home', link: { name: 'student.home' } },
                { ref: 'chats', title: vm.$t('common.my_chats'), icon: 'mdi-message-outline', link: { name: 'student.chats' }, count: 0, badge: 'messages' },
                { ref: 'routines', title: vm.$t('common.routines'), icon: '$training', link: { name: 'student.routines' } },
                { ref: 'habits', title: vm.$t('habits.title'), icon: 'mdi-checkbox-marked-outline', link: { name: 'student.habits' }, feature: 'habits' },
                { ref: 'statistics', title: vm.$t('common.statistics'), icon: '$statistics', link: { name: 'student.statistics' } },
                { ref: 'profile', title: vm.$t('common.profile'), icon: '$account_circle', link: { name: 'student.profile' } }
            ],
            student: [
                { ref: 'home', title: vm.$t('common.home'), icon: '$home', link: { name: 'student.home' } },
                { ref: 'coaches', title: vm.$tc('coach.coach', 2), icon: '$account_multiple', link: { name: 'student.coaches' } },
                { ref: 'routines', title: vm.$t('common.routines'), icon: '$training', link: { name: 'student.routines' } },
                { ref: 'profile', title: vm.$t('common.profile'), icon: '$account_circle', link: { name: 'student.profile' } }
            ],
            company: [
                { ref: 'home', title: vm.$t('common.home'), icon: '$home', link: { name: 'company.home' } },
                { ref: 'admin', title: vm.$t('common.admin_gym'), icon: '$company', link: { name: 'company.admin' }, count: 0, badge: 'messages' },
                { ref: 'library', title: vm.$t('common.library'), icon: '$training', link: { name: 'company.library' } },
                { ref: 'calendar', title: vm.$t('common.calendar'), icon: '$calendar', link: { name: 'company.calendar' } },
                { ref: 'statistics', title: vm.$t('common.statistics'), icon: '$statistics', link: { name: 'company.statistics' } },
                { ref: 'evaluations', title: vm.$t('common.evaluations'), icon: 'mdi-book-open-variant', link: { name: 'company.evaluations' } },
                { ref: 'business', title: vm.$t('business.title'), icon: 'mdi-briefcase-outline', link: { name: 'company.business' } },
                { ref: 'profile', title: vm.$t('common.profile'), icon: '$account_circle', link: { name: 'company.profile' } }
            ],
        }
    },
    computed: {
        currentItems(vm = this) {
            if (vm.accountType === 'company') return vm.company
            if (vm.accountType === 'coach') return vm.coach
            if (vm.account?.studentSubscription) return vm.studentWithCoach
            return vm.student
        },
        items(vm = this) {
            let items = vm.currentItems
            if (vm.isMobile) items = items.filter(item => item.ref !== 'evaluations' && item.ref !== 'business') // delete evaluations and business in mobile version
            const messagesItem = items.find(item => item.badge === 'messages')
            if (messagesItem) messagesItem.count = Math.max(0, Number(vm.account?.counter?.messages) || 0) + vm.unreadRecommendations + vm.unreadAlerts
            if (vm.isFreePlan) items = items.filter(item => item.ref !== 'statistics' && item.ref !== 'evaluations')
            items = items.filter(item => !item.feature || vm.can(item.feature))
            return items
        },
        navActiveRef(vm = this) {
            const name = vm.$route?.name || ''
            const hasRef = (ref) => vm.items.some(item => item.ref === ref)

            let best = null
            let bestLen = -1
            for (const item of vm.items) {
                const base = item.link?.name
                if (!base) continue
                if ((name === base || name.startsWith(`${base}.`)) && base.length > bestLen) {
                    best = item.ref
                    bestLen = base.length
                }
            }
            if (best) return best

            if (/\.(routines|exercises)(\.|$)/.test(name) && hasRef('library')) return 'library'
            if (/\.routine(\.|$)/.test(name) && hasRef('routines')) return 'routines'
            if (/\.zapping(\.|$)/.test(name) && hasRef('home')) return 'home'

            const path = vm.$route?.path || ''
            for (const item of vm.items) {
                if (!item.link?.name) continue
                let itemPath = ''
                try {
                    itemPath = vm.$router.resolve(item.link).route.path
                } catch (e) {
                    itemPath = ''
                }
                if (!itemPath) continue
                if ((path === itemPath || path.startsWith(`${itemPath}/`)) && itemPath.length > bestLen) {
                    best = item.ref
                    bestLen = itemPath.length
                }
            }
            return best
        }
    }
}
